import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#1f2937",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: "#1976d2",
    paddingBottom: 12,
    marginBottom: 20,
  },
  logoTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1976d2",
  },
  reportTitle: {
    fontSize: 14,
    color: "#1f2937",
    marginTop: 4,
  },
  filterPeriod: {
    fontSize: 9,
    color: "#6b7280",
    textAlign: "right",
  },
  cardsRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 20,
  },
  card: {
    flex: 1,
    padding: 14,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#f8fafc",
  },
  cardPrimary: {
    borderColor: "#1976d2",
    backgroundColor: "#e3f2fd",
  },
  cardSuccess: {
    borderColor: "#2e7d32",
    backgroundColor: "#e8f5e9",
  },
  cardLabel: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  cardValue: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1f2937",
  },
  cardSub: {
    fontSize: 9,
    color: "#6b7280",
    marginTop: 2,
  },
  tableWrap: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#1976d2",
    color: "#fff",
    fontSize: 9,
    fontWeight: "bold",
    paddingVertical: 8,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    fontSize: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9fafb",
  },
  colNome: { width: "22%", paddingRight: 4 },
  colDia: { width: "10%", paddingRight: 4, textAlign: "center" as const },
  colDias: { width: "12%", paddingRight: 4, textAlign: "center" as const },
  colFuncao: { width: "12%", paddingRight: 4 },
  colTipo: { width: "12%", paddingRight: 4 },
  colEmail: { width: "18%", paddingRight: 4 },
  colTel: { width: "8%", paddingRight: 4 },
  colCriacao: { width: "6%", textAlign: "center" as const },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 32,
    right: 32,
    textAlign: "center",
    fontSize: 8,
    color: "#9ca3af",
  },
});

type ItemAniversariante = {
  id: number;
  nome: string;
  funcao_produtor: string;
  tipo_produtor: string;
  email: string | null;
  email_contato: string;
  telefone1: string;
  telefone2: string;
  created_at: string;
  data_nasc: string;
};

const formatDiaAniversario = (dataNasc: string | undefined) => {
  if (!dataNasc || dataNasc.length < 10) return "-";
  const [, m, d] = dataNasc.split("-");
  return `${d}/${m}`;
};

const diasParaAniversario = (dataNasc: string | undefined): number | null => {
  if (!dataNasc || dataNasc.length < 10) return null;
  const [, mesStr, diaStr] = dataNasc.split("-");
  const mes = Number(mesStr) - 1;
  const dia = Number(diaStr);
  if (Number.isNaN(mes) || Number.isNaN(dia)) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  let proximo = new Date(hoje.getFullYear(), mes, dia);
  proximo.setHours(0, 0, 0, 0);
  if (proximo.getTime() < hoje.getTime()) {
    proximo = new Date(hoje.getFullYear() + 1, mes, dia);
    proximo.setHours(0, 0, 0, 0);
  }
  const diffMs = proximo.getTime() - hoje.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const labelDias = (dias: number | null) => {
  if (dias === null) return "-";
  if (dias === 0) return "Hoje";
  if (dias === 1) return "Amanhã";
  return `${dias} dias`;
};

const formatDateBR = (value: string) =>
  value ? new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "-";

export interface IReportAniversariantesPDFProps {
  list: ItemAniversariante[];
  aniversariantesHoje: number;
  total: number;
  startDate: string;
  endDate: string;
}

export const ReportAniversariantesProdutoresPDF = ({
  list,
  aniversariantesHoje,
  total,
  startDate,
  endDate,
}: IReportAniversariantesPDFProps) => (
  <Document>
    <Page size="A4" style={styles.page} orientation="landscape">
      <View style={styles.header}>
        <View>
          <Text style={styles.logoTitle}>Brokeris</Text>
          <Text style={styles.reportTitle}>Relatório de Aniversariantes - Produtores</Text>
        </View>
        <View style={styles.filterPeriod}>
          <Text>Período: {formatDateBR(startDate)} até {formatDateBR(endDate)}</Text>
        </View>
      </View>

      <View style={styles.cardsRow}>
        <View style={[styles.card, styles.cardPrimary]}>
          <Text style={styles.cardLabel}>Aniversariantes hoje</Text>
          <Text style={styles.cardValue}>{aniversariantesHoje}</Text>
          <Text style={styles.cardSub}>produtores</Text>
        </View>
        <View style={[styles.card, styles.cardSuccess]}>
          <Text style={styles.cardLabel}>Total no período</Text>
          <Text style={styles.cardValue}>{total}</Text>
          <Text style={styles.cardSub}>produtores</Text>
        </View>
      </View>

      <View style={styles.tableWrap}>
        <View style={styles.tableHeader}>
          <Text style={styles.colNome}>Nome</Text>
          <Text style={styles.colDia}>Dia aniv.</Text>
          <Text style={styles.colDias}>Dias p/ aniv.</Text>
          <Text style={styles.colFuncao}>Função</Text>
          <Text style={styles.colTipo}>Tipo</Text>
          <Text style={styles.colEmail}>Email</Text>
          <Text style={styles.colTel}>Telefone</Text>
          <Text style={styles.colCriacao}>Criação</Text>
        </View>
        {list.map((item, index) => {
          const dias = diasParaAniversario(item.data_nasc);
          return (
            <View
              key={item.id}
              style={index % 2 === 1 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}
            >
              <Text style={styles.colNome}>{item.nome}</Text>
              <Text style={styles.colDia}>{formatDiaAniversario(item.data_nasc)}</Text>
              <Text style={styles.colDias}>{labelDias(dias)}</Text>
              <Text style={styles.colFuncao}>{item.funcao_produtor || "-"}</Text>
              <Text style={styles.colTipo}>{item.tipo_produtor || "-"}</Text>
              <Text style={styles.colEmail}>{item.email || item.email_contato || "-"}</Text>
              <Text style={styles.colTel}>{item.telefone1 || item.telefone2 || "-"}</Text>
              <Text style={styles.colCriacao}>{formatDateBR(item.created_at)}</Text>
            </View>
          );
        })}
      </View>

      <Text style={styles.footer}>
        Brokeris - Relatório de Aniversariantes (Produtores) - Gerado em{" "}
        {new Date().toLocaleDateString("pt-BR")} às {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
      </Text>
    </Page>
  </Document>
);

/**
 * Gera e faz download do PDF do relatório de aniversariantes.
 */
export const handleDownloadAniversariantesPDF = async (props: IReportAniversariantesPDFProps) => {
  const blob = await pdf(<ReportAniversariantesProdutoresPDF {...props} />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `aniversariantes-produtores-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
